/**
 * Telling the MCP server that one of its surfaces moved, so an external
 * subscriber can be **pushed** "read again" instead of polling.
 *
 * This is the counterpart of `uiRefresh.js`: that module tells the open UI that
 * an external agent changed something; this one tells external agents that
 * *anything* changed. Both hang off the same two nonces, which is deliberate —
 * `bumpDocumentsTree()` and `bumpBoards()` are the app's signal for "the tree
 * moved", bumped by the editor's title save, the sync engine, the team-boards
 * realtime path and the in-app assistant. Inventing a second signal beside a
 * working one is the mistake the boards section of `uiRefresh.js` warns about.
 *
 * **Two UI surfaces call `notifyMcpSurfaceChanged` directly instead** (found
 * 13.09.2026 — until then neither notified at all): the Documents sidebar's
 * create / rename / move / delete (it reloads itself, so a nonce bump would be
 * a second read) and the Boards panel's create / edit / delete / share (a
 * boardsVersion bump re-reads the open board mid-drag). Neither is in the
 * stores, because MCP's own writes go through the same store functions.
 *
 * ## What crosses, and what cannot
 *
 * **A surface NAME and an origin. Nothing else.** No record, no id, no scope,
 * no body. That is not a rule we have to remember — it is the shape of the
 * call. `notifications/resources/list_changed` names nothing, so there is
 * nothing for an id to be *for*, and the Rust command's argument list is
 * pinned to exactly these two strings by a source scan
 * (`mcp/mod.rs::source_pins`). A field that cannot be added cannot leak.
 *
 * The two content-carrying publishers next door — `publishDocumentChanges` and
 * `publishBoardChanges` — are channels for the app's **own UI** and stay
 * internal. They must never be wired into this module.
 *
 * ## Only two surfaces, and that is structural
 *
 * Documents and boards are the only surfaces with resource URIs at all
 * (`src-tauri/src/mcp/resources.rs`). A calendar occurrence has no stable
 * record id (**O24**) and collections have no change nonce and must not gain
 * one (**O30**, `uiRefresh.js`'s Collections section) — so neither could be
 * named in a notification even if we wanted to. Rust ignores any other surface
 * string; this list exists so the intent is legible on the JS side too.
 *
 * ## The agent is not told about its own write
 *
 * MCP's own writes reach the same bumps as everyone else's, so they arrive
 * tagged `origin: 'mcp'` and Rust drops them. Without it an agent is notified
 * of its own write and two agents cross-notify into a loop; the honest signal
 * is "something *else* changed".
 *
 * ## Best-effort, always
 *
 * A bump is a UI signal on a hot path. Nothing here may throw into its caller,
 * and nothing here may make a landed write look failed — so every failure is
 * swallowed, exactly like `uiRefresh.js`'s publish and reveal. With the MCP
 * server off, the command is a no-op in Rust.
 */

/** The only surfaces a change notification can be about. */
export const NOTIFIABLE_SURFACES = Object.freeze(['documents', 'boards']);

/** Where a change came from. `'mcp'` is dropped in Rust (self-echo suppression). */
export const ORIGIN_APP = 'app';
export const ORIGIN_MCP = 'mcp';

/**
 * Surfaces waiting to be sent. A `Set`, so a burst of bumps in one tick
 * collapses to one invoke per surface — the notification is an idempotent
 * "call the list again", so a second copy carries no information.
 */
const pending = new Set();
let scheduled = false;

async function flush() {
  scheduled = false;
  const batch = [...pending];
  pending.clear();
  for (const key of batch) {
    const [surface, origin] = key.split('|');
    try {
      // Dynamic import: no `@tauri-apps/api/*` static import in shared app
      // code — there is a test that pins this.
      const { invoke } = await import('@tauri-apps/api/core');
      await invoke('mcp_notify_changed', { surface, origin });
    } catch (err) {
      // Not running under Tauri, the server is off, the command is not
      // registered: none of these may disturb the caller.
      if (process.env.NODE_ENV !== 'test') {
        console.debug('[mcp] change notify skipped:', surface, err);
      }
    }
  }
}

/**
 * Tell the MCP server that `surface` changed.
 *
 * Returns nothing and never rejects. Safe to call from a zustand store's
 * setter, which is where it is called from.
 */
export function notifyMcpSurfaceChanged(surface, origin = ORIGIN_APP) {
  if (!NOTIFIABLE_SURFACES.includes(surface)) return;
  pending.add(`${surface}|${origin === ORIGIN_MCP ? ORIGIN_MCP : ORIGIN_APP}`);
  if (scheduled) return;
  scheduled = true;
  Promise.resolve().then(flush);
}

/** Test seam: drop anything queued, so one test cannot leak into the next. */
export function __resetMcpNotifyQueue() {
  pending.clear();
  scheduled = false;
}
